<header class="relative px-4 pb-4 pt-2">
  <h1 class="text-2xl font-semibold text-greyscale-900">Projects</h1>
  <p class="text-sm text-greyscale-600">Manage carbon offset projects</p>
  <p-button label="Create Project" icon="pi pi-plus" routerLink="new" class="absolute right-4 top-2" />
</header>

<main class="p-4">
  <p-table
    #dt
    [value]="projectList"
    [paginator]="true"
    [rows]="20"
    [globalFilterFields]="['nameEN', 'slug', 'location']"
    styleClass="p-datatable-sm"
  >
    <ng-template #caption>
      <div class="flex flex-wrap items-end gap-4">
        <div class="min-w-[140px]">
          <span class="mb-1 block text-sm font-medium">Status</span>
          <p-select
            [options]="statusOptions"
            [(ngModel)]="statusFilter"
            (onChange)="onFilterChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="All statuses"
            [style]="{ width: '100%' }"
          />
        </div>
        <div class="min-w-[160px]">
          <span class="mb-1 block text-sm font-medium">Type</span>
          <p-select
            [options]="typeOptions"
            [(ngModel)]="typeFilter"
            (onChange)="onFilterChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="All types"
            [style]="{ width: '100%' }"
          />
        </div>
        <div class="min-w-[140px]">
          <span class="mb-1 block text-sm font-medium">Region</span>
          <p-select
            [options]="regionOptions"
            [(ngModel)]="regionFilter"
            (onChange)="onFilterChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="All regions"
            [style]="{ width: '100%' }"
          />
        </div>
        <div class="min-w-[160px]">
          <span class="mb-1 block text-sm font-medium">Certification</span>
          <p-select
            [options]="certificationOptions"
            [(ngModel)]="certificationFilter"
            (onChange)="onFilterChange()"
            optionLabel="label"
            optionValue="value"
            placeholder="All certifications"
            [style]="{ width: '100%' }"
          />
        </div>
        <div>
          <p-button
            icon="pi pi-filter-slash"
            (onClick)="resetFilters()"
            [text]="true"
            [rounded]="true"
            pTooltip="Reset filters"
          />
        </div>
        <div class="ml-auto min-w-[200px]">
          <span class="mb-1 block text-sm font-medium">Search</span>
          <p-iconfield iconPosition="left">
            <p-inputicon>
              <i class="pi pi-search"></i>
            </p-inputicon>
            <input
              pInputText
              type="text"
              [(ngModel)]="globalFilter"
              (ngModelChange)="dt.filterGlobal(globalFilter, 'contains')"
              placeholder="Search projects..."
              class="w-full"
            />
          </p-iconfield>
        </div>
      </div>
    </ng-template>

    <ng-template #header>
      <tr>
        <th>Name</th>
        <th>Status</th>
        <th>Type</th>
        <th>Region</th>
        <th>Price/Tonne</th>
        <th>Certification</th>
        <th>Flags</th>
      </tr>
    </ng-template>

    <ng-template #emptymessage>
      <tr>
        <td colspan="7" class="text-center py-12">
          <div class="flex flex-col items-center gap-4">
            <i class="pi pi-inbox text-6xl text-greyscale-300"></i>
            <div class="flex flex-col gap-1">
              <h3 class="text-lg font-semibold text-greyscale-700">No projects found</h3>
              <p class="text-sm text-greyscale-500">
                @if (globalFilter || statusFilter || typeFilter || regionFilter || certificationFilter) {
                  Try adjusting your filters or search criteria
                } @else {
                  Get started by creating your first project
                }
              </p>
            </div>
            @if (!globalFilter && !statusFilter && !typeFilter && !regionFilter && !certificationFilter) {
              <p-button
                label="Create Project"
                icon="pi pi-plus"
                routerLink="new"
                styleClass="mt-2"
              />
            }
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template #body let-project>
      <tr
        class="cursor-pointer hover:bg-gray-50 h-14"
        (click)="editProject(project.id)"
      >
        <td>
          <div class="flex flex-col">
            <span class="font-medium">{{ project.nameEN }}</span>
            <span class="text-xs text-gray-500">{{ project.slug }}</span>
          </div>
        </td>

        <td>
          <span
            class="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
            [ngClass]="getStatusClasses(project.status)"
          >
            {{ getStatusLabel(project.status) }}
          </span>
        </td>

        <td>
          <div class="flex items-center gap-2">
            <lucide-angular
              [img]="getTypeIcon(project.projectType)"
              class="text-primary-600"
              [size]="16"
            />
            <span>{{ getTypeLabel(project.projectType) }}</span>
          </div>
        </td>

        <td>{{ getRegionLabel(project.region) }}</td>

        <td class="font-medium">{{ formatPrice(project.pricePerTonne) }}</td>

        <td>
          <div class="flex items-center gap-1">
            <span [class.text-primary-600]="project.isCertifiedOffset" [class.font-medium]="project.isCertifiedOffset">
              {{ getCertificationLabel(project.certificationStandard) }}
            </span>
            @if (project.isCertifiedOffset) {
              <lucide-angular
                [img]="badgeCheckIcon"
                class="text-primary-500"
                [size]="14"
                pTooltip="Certified offset"
                tooltipPosition="top"
              />
            }
          </div>
        </td>

        <td>
          <div class="flex gap-1">
            @if (project.isDefault) {
              <lucide-angular
                [img]="starIcon"
                class="text-primary-400"
                [size]="16"
                pTooltip="Default project"
                tooltipPosition="top"
              />
            }
            @if (project.isFeatured) {
              <lucide-angular
                [img]="pinIcon"
                class="text-primary-300"
                [size]="16"
                pTooltip="Featured project"
                tooltipPosition="top"
              />
            }
            @if (!project.isVisible) {
              <lucide-angular
                [img]="eyeOffIcon"
                class="text-greyscale-400"
                [size]="16"
                pTooltip="Hidden from users"
                tooltipPosition="top"
              />
            }
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</main>

<p-confirmdialog rejectButtonStyleClass="p-button-outlined" />
<p-toast />
